import { db } from '../firebase';
import { centralAdminApi } from '../../../lib/centralTenantAdminApi';
import { syncTenantLicense } from '../../../lib/centralTenantApi';
import {
  addAdminToTenant as addAdminToTenantCore,
  addCreditsToTenant as addCreditsToTenantCore,
  assignPlanToTenant as assignPlanToTenantCore,
  createTenant as createTenantCore,
  fetchTenantAdmins as fetchTenantAdminsCore,
  fetchUsersAndTenants as fetchUsersAndTenantsCore,
  getAllTenants,
  getTenantPricingCollectionNames,
  removeAdminFromTenant as removeAdminFromTenantCore,
  setTenantPricingCollectionNames,
  setTenantStatus as setTenantStatusCore,
  updateTenant as updateTenantCore,
} from '../../../utils/tenantPricingModule';

/*
 * Tenant service adapter.
 *
 * OVplan mode uses the local Firestore helpers directly.
 * CCS mode calls the central admin callable APIs, but still reads the local
 * mirror collections for display. The mirror only updates after explicit
 * refresh/fetch actions.
 */

const DEFAULT_MODE = String(
  process.env.REACT_APP_TENANT_MANAGEMENT_MODE || process.env.REACT_APP_APP_ROLE || 'ovplan'
).toLowerCase();

const withDb = (options = {}) => ({ db, ...options });

const normalizeMode = (mode = DEFAULT_MODE) => String(mode || DEFAULT_MODE).toLowerCase();

const syncTenantToCentralQuietly = (tenantIdOrDocId, result = {}) => {
  const tenantId = result?.tenant?.tenantId
    || result?.after?.tenantId
    || result?.tenantId
    || tenantIdOrDocId;
  if (!tenantId) return;
  syncTenantLicense(tenantId).catch((error) => {
    console.warn('[TenantManagement] CCS mirror sync failed:', error?.message || error);
  });
};

export const getTenantManagementMode = () => DEFAULT_MODE;

export const isCentralTenantManagementMode = (mode = DEFAULT_MODE) => {
  const normalized = normalizeMode(mode);
  return normalized === 'ccs' || normalized === 'central';
};

const normalizeResult = (result, fallback = {}) => {
  if (!result) {
    return { success: false, reason: 'API_UNAVAILABLE', ...fallback };
  }
  if (result.success === false) {
    return { ...fallback, ...result };
  }
  return result;
};

export const setTenantManagementCollectionNames = (overrides = {}) =>
  setTenantPricingCollectionNames(overrides);

export const getTenantManagementCollectionNames = () =>
  getTenantPricingCollectionNames();

export const fetchUsersAndTenants = async (options = {}) =>
  fetchUsersAndTenantsCore(withDb(options));

export const fetchTenants = async (options = {}) =>
  getAllTenants(withDb(options));

export const refreshTenants = async (options = {}) => {
  const mode = normalizeMode(options.mode);
  if (isCentralTenantManagementMode(mode)) {
    return normalizeResult(await centralAdminApi.refreshTenants(options.payload));
  }

  const tenants = await fetchUsersAndTenantsCore(withDb(options));
  return { success: true, ...tenants };
};

export const createTenant = async (payload = {}, options = {}) => {
  const mode = normalizeMode(options.mode);
  if (isCentralTenantManagementMode(mode)) {
    return normalizeResult(await centralAdminApi.createTenant(payload));
  }

  const result = await createTenantCore(payload, withDb(options));
  syncTenantToCentralQuietly(result?.tenant?.tenantId || result?.tenantId || payload.tenantId, result);
  return result;
};

export const updateTenant = async (tenantIdOrDocId, updates = {}, options = {}) => {
  const mode = normalizeMode(options.mode);
  if (isCentralTenantManagementMode(mode)) {
    return normalizeResult(await centralAdminApi.updateTenant({
      tenantId: tenantIdOrDocId,
      ...updates,
    }));
  }

  const result = await updateTenantCore(tenantIdOrDocId, updates, withDb(options));
  syncTenantToCentralQuietly(tenantIdOrDocId, result);
  return {
    ...result,
    tenant: result.tenant || result.after,
    sourceVersion: result.tenant?.sourceVersion || result.after?.sourceVersion,
  };
};

export const assignPlanToTenant = async (tenantIdOrDocId, planName, options = {}) => {
  const mode = normalizeMode(options.mode);
  if (isCentralTenantManagementMode(mode)) {
    return normalizeResult(await centralAdminApi.assignPlanToTenant({
      tenantId: tenantIdOrDocId,
      planId: planName,
    }));
  }

  const result = await assignPlanToTenantCore(tenantIdOrDocId, planName, withDb(options));
  syncTenantToCentralQuietly(tenantIdOrDocId, result);
  return {
    ...result,
    planId: result.planId || planName,
    license: result.license || result.tenant?.license || result.after?.license,
    tenant: result.tenant || result.after,
    remainingCredits: result.remainingCredits || result.tenant?.remainingCredits || result.after?.remainingCredits,
    usedCredits: result.usedCredits || result.tenant?.usedCredits || result.after?.usedCredits,
    sourceVersion: result.sourceVersion || result.tenant?.sourceVersion || result.after?.sourceVersion,
  };
};

export const addCreditsToTenant = async (tenantIdOrDocId, creditsToAdd, options = {}) => {
  const mode = normalizeMode(options.mode);
  if (isCentralTenantManagementMode(mode)) {
    return normalizeResult(await centralAdminApi.addCreditsToTenant({
      tenantId: tenantIdOrDocId,
      creditsToAdd,
    }));
  }

  const result = await addCreditsToTenantCore(tenantIdOrDocId, creditsToAdd, withDb(options));
  syncTenantToCentralQuietly(tenantIdOrDocId, result);
  return result;
};

export const setTenantStatus = async (tenantIdOrDocId, status, options = {}) => {
  const mode = normalizeMode(options.mode);
  if (isCentralTenantManagementMode(mode)) {
    return normalizeResult(await centralAdminApi.updateTenantStatus({
      tenantId: tenantIdOrDocId,
      status,
    }));
  }

  const result = await setTenantStatusCore(tenantIdOrDocId, status, withDb(options));
  syncTenantToCentralQuietly(tenantIdOrDocId, result);
  return {
    ...result,
    status: result.status || status,
    tenant: result.tenant || result.after,
    sourceVersion: result.sourceVersion || result.tenant?.sourceVersion || result.after?.sourceVersion,
  };
};

export const addAdminToTenant = async (tenantId, adminEmail, adminName, options = {}) => {
  const mode = normalizeMode(options.mode);
  if (isCentralTenantManagementMode(mode)) {
    return normalizeResult(await centralAdminApi.addAdminToTenant({
      tenantId,
      adminEmail,
      adminName,
      tenantName: options.tenantName || options.tenantDisplayName || '',
    }));
  }

  const result = await addAdminToTenantCore(tenantId, adminEmail, adminName, withDb(options));
  syncTenantToCentralQuietly(tenantId, result);
  return result;
};

export const replaceTenantAdmin = async (tenantId, adminId, replacement = {}, options = {}) => {
  const mode = normalizeMode(options.mode);
  if (isCentralTenantManagementMode(mode)) {
    return normalizeResult(await centralAdminApi.replaceTenantAdmin({
      tenantId,
      adminId,
      replacementEmail: replacement.email || replacement.adminEmail || '',
      replacementName: replacement.name || replacement.adminName || '',
      tenantName: replacement.tenantName || options.tenantName || '',
    }));
  }

  const result = await removeAdminFromTenantCore(tenantId, adminId, replacement, withDb(options));
  syncTenantToCentralQuietly(tenantId, result);
  return result;
};

export const removeAdminFromTenant = replaceTenantAdmin;

export const fetchTenantAdmins = async (tenantId, options = {}) =>
  fetchTenantAdminsCore(tenantId, withDb(options));
